import React, { useEffect, useState } from "react";
import "./CommentSection.scss";
import {
  Avatar,
  Button,
  Input,
  Typography,
  Dropdown,
  Spin,
  Tooltip,
} from "antd";
import {
  DownOutlined,
  FieldTimeOutlined,
  LikeFilled,
  LikeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import LikesModal from "../../../LikesModal/LikesModal";
import defaultImage from "../../../../assets/default_image_patient.png";
import { useDispatch, useSelector } from "react-redux";
import {
  getComments,
  postComment,
  toggleCommentLike,
} from "../../../../features/comments/commentsActions";
import { resetCommentsError } from "../../../../features/comments/commentsSlice";
import CanDoAction from "../../../../common/services/Permissions/Can";
import Utils from "../../../../utils";

const { TextArea } = Input;

export default function CommentSection({
  token,
  patient,
  set_is_signin_opened,
  isDetailOpened,
  openNotificationWithIcon,
}) {
  const { isAuthenticated, user } = useSelector((state) => state?.auth);
  const {
    commentsData: { results: comments, meta },
    loading,
    error,
  } = useSelector((state) => state?.comments);
  const dispatch = useDispatch();

  const arrangeOptions = [
    {
      key: "best",
      label: "Best",
    },
    {
      key: "created_at",
      label: "Oldest",
    },
    {
      key: "",
      label: "Reset",
    },
  ];

  const [hasFetchedComments, setHasFetchedComments] = useState(false);
  const [selectedOptionKey, setSelectedOptionKey] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [comment, setComment] = useState("");
  const [likedComments, setLikedComments] = useState(new Set());
  const [likeCounts, setLikeCounts] = useState({});
  const [likesModalCommentId, setLikesModalCommentId] = useState(null);
  const [likesInFlight, setLikesInFlight] = useState(new Set());

  useEffect(() => {
    if (comments?.length) {
      setLikedComments((prev) => {
        const next = new Set(prev);
        comments.forEach((c) => {
          if (c.is_liked) {
            next.add(c.id);
          } else {
            next.delete(c.id);
          }
        });
        return next;
      });
      setLikeCounts((prev) => {
        const next = { ...prev };
        comments.forEach((c) => {
          next[c.id] = c.likes_count ?? 0;
        });
        return next;
      });
    }
  }, [comments]);

  const handleLikeToggle = (commentId, baseLikeCount) => {
    if (!isAuthenticated) {
      set_is_signin_opened(true);
      return;
    }
    if (likesInFlight.has(commentId)) return;

    const wasLiked = likedComments.has(commentId);
    const prevCount = likeCounts[commentId] ?? baseLikeCount;

    // Optimistic update
    setLikedComments((prev) => {
      const next = new Set(prev);
      if (wasLiked) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
    setLikeCounts((prev) => ({
      ...prev,
      [commentId]: wasLiked ? prevCount - 1 : prevCount + 1,
    }));

    setLikesInFlight((prev) => new Set(prev).add(commentId));
    dispatch(toggleCommentLike({ patientId: patient?.id, commentId }))?.then(
      (res) => {
        setLikesInFlight((prev) => {
          const next = new Set(prev);
          next.delete(commentId);
          return next;
        });
        if (res?.type === "toggle_comment_like/fulfilled") {
          setLikedComments((prev) => {
            const next = new Set(prev);
            if (res.payload.is_liked) {
              next.add(commentId);
            } else {
              next.delete(commentId);
            }
            return next;
          });
          setLikeCounts((prev) => ({
            ...prev,
            [commentId]: res.payload.likes_count,
          }));
        } else {
          // Rollback on failure
          setLikedComments((prev) => {
            const next = new Set(prev);
            if (wasLiked) {
              next.add(commentId);
            } else {
              next.delete(commentId);
            }
            return next;
          });
          setLikeCounts((prev) => ({
            ...prev,
            [commentId]: prevCount,
          }));
          const error = res?.payload || { Error: "An error occurred" };
          for (let key in error) {
            if (typeof error[key] === "string") {
              openNotificationWithIcon("error", "Error", error[key]);
              break;
            }
          }
        }
      }
    );
  };

  const getCommentsByfilter = (params) => {
    dispatch(
      getComments({
        id: patient?.id,
        page: page ? page : undefined,
        per_page: perPage ? perPage : undefined,
        order_by: selectedOptionKey ? selectedOptionKey : undefined,
        ...params,
      })
    );
  };

  const handleDropdownClick = ({ key }) => {
    setSelectedOptionKey(key);
    setPage(1);
    getCommentsByfilter({
      order_by: key ? key : undefined,
      page: 1,
    });
  };

  const onLoadMore = () => {
    setPerPage(perPage + 10);
    getCommentsByfilter({
      page,
      per_page: perPage + 10,
    });
  };

  useEffect(() => {
    if (isDetailOpened && !hasFetchedComments) {
      dispatch(
        getComments({
          id: patient?.id,
          page: 1,
          per_page: 10,
        })
      );
      setHasFetchedComments(true);
    }
  }, [dispatch, patient, isDetailOpened, hasFetchedComments]);

  useEffect(() => {
    if (isDetailOpened) {
      if (error && !loading) {
        if (typeof error === "object" && !Array.isArray(error)) {
          for (let key in error) {
            if (typeof key === "string" && typeof error[key] === "string") {
              openNotificationWithIcon("error", key, error[key]);
              break;
            }
          }
        } else {
          openNotificationWithIcon("error", "Error", "Something went wrong !");
        }
        dispatch(resetCommentsError());
      }
    }
  }, [error, loading, openNotificationWithIcon, dispatch, isDetailOpened]);

  const addComment = (params) => {
    dispatch(postComment(params))?.then((res) => {
      if (res?.type === "post_comment/fulfilled") {
        setComment("");
        getCommentsByfilter();
      }
    });
  };

  return (
    <div className="commentSection-cont">
      <CanDoAction isOrganization={user?.is_organization && isAuthenticated}>
        <div className="commentSection-cont__addComment">
          <div className="commentSection-cont__addComment__commentWrapper">
            <Avatar
              size={35}
              src={user?.picture_url || defaultImage}
              icon={<UserOutlined />}
              style={{
                border: `1px solid ${token?.colorSecondaryLogo}`,
                flexShrink: 0,
              }}
            />
            <TextArea
              style={{
                resize: "none",
              }}
              placeholder="What do yo think ?"
              disabled={loading}
              value={comment}
              rows={3}
              onChange={(e) => {
                setComment(e.target.value);
              }}
            />
          </div>
          <div className="commentSection-cont__addComment__addCont">
            <Typography.Text>
              🧐 Makers appreciate thoughtful comments
            </Typography.Text>
            <Button
              className="commentSection-cont__addComment__addCont__btn"
              type="primary"
              loading={loading}
              onClick={() => {
                if (!isAuthenticated) {
                  set_is_signin_opened(true);
                } else {
                  if (comment) {
                    addComment({
                      id: patient?.id,
                      content: comment,
                    });
                  } else {
                    openNotificationWithIcon(
                      "warning",
                      "Warning",
                      "Comment can't be blank."
                    );
                  }
                }
              }}>
              {isAuthenticated ? "Comment" : "Login To Comment"}
            </Button>
          </div>
        </div>
      </CanDoAction>
      <div className="commentSection-cont__commentsCont">
        {comments?.length ? (
          <div className="commentSection-cont__commentsCont__filters">
            <Dropdown
              className="commentSection-cont__commentsCont__filters__dropDown"
              trigger={["click"]}
              menu={{
                items: arrangeOptions,
                onClick: handleDropdownClick,
              }}>
              <div className="commentSection-cont__commentsCont__filters__dropDown__content">
                {loading ? (
                  <Spin />
                ) : (
                  <>
                    <Typography.Text>
                      {selectedOptionKey === "best"
                        ? "Best"
                        : selectedOptionKey === "created_at"
                          ? "Oldest"
                          : "Sort By"}
                    </Typography.Text>
                    <DownOutlined
                      style={{
                        fontSize: "14px",
                      }}
                    />
                  </>
                )}
              </div>
            </Dropdown>
          </div>
        ) : null}
        <div className="commentSection-cont__commentsCont__comments">
          {comments?.map((comment) => (
            <div
              key={comment?.id}
              style={{
                width: "100%",
              }}>
              <div className="commentSection-cont__commentsCont__comments__content">
                <Avatar
                  size={35}
                  src={comment?.created_by?.picture_url || defaultImage}
                  icon={<UserOutlined />}
                  style={{
                    border: `1px solid ${token?.colorSecondaryLogo}`,
                    flexShrink: 0,
                  }}
                />
                <div className="commentSection-cont__commentsCont__comments__content__commentContent">
                  <Typography.Text
                    strong
                    ellipsis={{
                      tooltip: {
                        title: comment?.author_name || "",
                      },
                    }}>
                    {comment?.author_name || ""}
                  </Typography.Text>
                  <Typography.Text
                    style={{
                      width: "100%",
                    }}>
                    {comment?.content ? comment?.content : ""}
                  </Typography.Text>
                  <div className="commentSection-cont__commentsCont__comments__btnWrapper">
                    <div className="commentSection-cont__commentsCont__comments__btnWrapper__likeRow">
                      <Button
                        size="small"
                        style={{
                          border: "none",
                          color: likedComments.has(comment?.id)
                            ? token?.colorPrimary
                            : undefined,
                        }}
                        icon={
                          likedComments.has(comment?.id) ? (
                            <LikeFilled />
                          ) : (
                            <LikeOutlined />
                          )
                        }
                        loading={likesInFlight.has(comment?.id)}
                        disabled={likesInFlight.has(comment?.id)}
                        onClick={() =>
                          handleLikeToggle(
                            comment?.id,
                            comment?.likes_count ?? 0
                          )
                        }
                      />
                      {(likeCounts[comment?.id] ?? comment?.likes_count ?? 0) >
                        0 && (
                        <Typography.Text
                          className="commentSection-cont__commentsCont__comments__btnWrapper__likeRow__count"
                          style={{
                            color: token?.colorPrimary,
                          }}
                          onClick={() => setLikesModalCommentId(comment?.id)}>
                          {likeCounts[comment?.id] ?? comment?.likes_count ?? 0}{" "}
                          {(likeCounts[comment?.id] ??
                            comment?.likes_count ??
                            0) === 1
                            ? "like"
                            : "likes"}
                        </Typography.Text>
                      )}
                    </div>
                    <div className="commentSection-cont__commentsCont__comments__btnWrapper__dateWrapper">
                      <FieldTimeOutlined />
                      <Tooltip
                        title={Utils?.format_date_time(comment?.created_at)}
                        destroyOnHidden={true}>
                        <Typography.Text ellipsis>
                          {Utils?.getTimeByMeasurment(comment?.created_at)}
                        </Typography.Text>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {comments?.length < meta?.count ? (
          <div className="commentSection-cont__commentsCont__loadWrapper">
            <Button loading={loading} onClick={onLoadMore} shape="round" block>
              {`${!loading ? meta?.count - perPage : ""} More Comments`}
            </Button>
          </div>
        ) : null}
      </div>
      <LikesModal
        token={token}
        open={likesModalCommentId !== null}
        onCancel={() => setLikesModalCommentId(null)}
        patientId={patient?.id}
        commentId={likesModalCommentId}
        openNotificationWithIcon={openNotificationWithIcon}
      />
    </div>
  );
}
